import { MasterLegalAgreementType, RelationshipAction, SpreadsheetMapping, ValidationIssue } from 'api'
import { Locale, Translation } from 'locale'
import { gatherIssues, parseEnum, parseEnumArray, parseString, SpreadsheetParseResult } from '../upload/model'

export interface TradingRelationshipAttributes {
  relationshipAction: RelationshipAction | null
  buyOrganisation: string | null
  buyTradingEntity: string | null
  sellOrganisation: string | null
  sellTradingEntity: string | null
  usSponsoredClearing: boolean
  usSponsoredClearingDefault: boolean
  masterLegalAgreements: MasterLegalAgreementType[]
  defaultAgreement: MasterLegalAgreementType | null
}

interface TradingRelationshipSpreadsheetParseRowResult {
  tradingRelationship: TradingRelationshipAttributes
  parseIssues: ValidationIssue[]
}

export interface TradingRelationshipSpreadsheetParseResult extends SpreadsheetParseResult {
  tradingRelationships: TradingRelationshipAttributes[]
}

export function parseTradingRelationshipSpreadsheet(
  filename: string,
  rows: any[],
  spreadsheetMapping: SpreadsheetMapping
): TradingRelationshipSpreadsheetParseResult {
  return rows.reduce<TradingRelationshipSpreadsheetParseResult>(
    (spreadsheetResult, row, rowIdx) => {
      const rowResult = parseRow(row, rowIdx + 1, spreadsheetMapping)

      spreadsheetResult.issues.push(...rowResult.parseIssues)

      if (rowResult.tradingRelationship && rowResult.parseIssues.length === 0) {
        spreadsheetResult.tradingRelationships.push(rowResult.tradingRelationship)
      }

      return spreadsheetResult
    },
    { filename, rows, issues: [], tradingRelationships: [] }
  )
}

function parseRow(row: any, rowNum: number, spreadsheetMapping: SpreadsheetMapping): TradingRelationshipSpreadsheetParseRowResult {
  const relationshipActionResult = parseEnum<RelationshipAction>(
    row,
    rowNum,
    spreadsheetMapping.relationshipAction,
    spreadsheetMapping.relationshipAction.values,
    Locale.translate(Translation.TC_RELATIONSHIP_ACTION),
    false,
    true
  )
  const buyOrganisationResult = parseString(
    row,
    rowNum,
    spreadsheetMapping.buyOrganisation,
    Locale.translate(Translation.TC_BUY_ORGANISATION),
    true
  )
  const buyTradingEntityResult = parseString(
    row,
    rowNum,
    spreadsheetMapping.buyTradingEntity,
    Locale.translate(Translation.TC_BUY_TRADING_ENTITY),
    true
  )
  const sellOrganisationResult = parseString(
    row,
    rowNum,
    spreadsheetMapping.sellOrganisation,
    Locale.translate(Translation.TC_SELL_ORGANISATION),
    true
  )
  const sellTradingEntityResult = parseString(
    row,
    rowNum,
    spreadsheetMapping.sellTradingEntity,
    Locale.translate(Translation.TC_SELL_TRADING_ENTITY),
    true
  )
  const usSponsoredClearingResult = parseString(
    row,
    rowNum,
    spreadsheetMapping.usSponsoredClearing,
    Locale.translate(Translation.TC_US_SC_ENABLED),
    true
  )
  const usSponsoredClearingDefaultResult = parseString(
    row,
    rowNum,
    spreadsheetMapping.usSponsoredClearingDefault,
    Locale.translate(Translation.TC_US_SC_DEFAULT),
    true
  )
  const masterLegalAgreementsResult = parseEnumArray<MasterLegalAgreementType>(
    row,
    rowNum,
    spreadsheetMapping.masterLegalAgreements,
    spreadsheetMapping.masterLegalAgreements.values,
    Locale.translate(Translation.TC_MASTER_LEGAL_AGREEMENTS),
    false,
    false
  )
  const defaultAgreementResult = parseEnum<MasterLegalAgreementType>(
    row,
    rowNum,
    spreadsheetMapping.defaultAgreement,
    spreadsheetMapping.defaultAgreement.values,
    Locale.translate(Translation.TC_DEFAULT_AGREEMENT),
    false,
    false
  )

  const parseIssues = gatherIssues(
    relationshipActionResult,
    buyOrganisationResult,
    buyTradingEntityResult,
    sellOrganisationResult,
    sellTradingEntityResult,
    usSponsoredClearingResult,
    usSponsoredClearingDefaultResult,
    masterLegalAgreementsResult,
    defaultAgreementResult
  )

  const usSponsoredClearing = usSponsoredClearingResult.isValue && usSponsoredClearingResult.value.toLocaleLowerCase() === 'y'

  return {
    parseIssues,
    tradingRelationship: {
      relationshipAction: relationshipActionResult.isValue ? relationshipActionResult.value : null,
      buyOrganisation: buyOrganisationResult.isValue ? buyOrganisationResult.value : null,
      buyTradingEntity: buyTradingEntityResult.isValue ? buyTradingEntityResult.value : null,
      sellOrganisation: sellOrganisationResult.isValue ? sellOrganisationResult.value : null,
      sellTradingEntity: sellTradingEntityResult.isValue ? sellTradingEntityResult.value : null,
      usSponsoredClearing,
      usSponsoredClearingDefault:
        usSponsoredClearing &&
        usSponsoredClearingDefaultResult.isValue &&
        usSponsoredClearingDefaultResult.value.toLocaleLowerCase() === 'y',
      masterLegalAgreements: masterLegalAgreementsResult.isValue ? masterLegalAgreementsResult.value : [],
      defaultAgreement: defaultAgreementResult.isValue ? defaultAgreementResult.value : null
    }
  }
}
